import { z } from 'zod';

/**
 * Anonymous usage events (F7 D-2, AC-2.3): `POST /api/track`, written to the analytics D1 database.
 *
 * No personal data: the only identifier is `anonId`, a random UUID the client keeps on the device
 * (privacy policy, F1). Every object is strict, so a client that adds a field (email, name, user id,
 * free text ...) gets a 422 instead of having it stored.
 */

/** Event kinds the API counts. Adding a kind is additive; renaming one splits the history. */
export const USAGE_EVENT_KINDS = [
  'page_view',
  'theory_exam_started',
  'theory_exam_finished',
  'sim_exam_started',
  'sim_exam_finished',
] as const;

/** Kinds that may carry `passed` (an exam result). */
export const USAGE_FINISHED_KINDS = ['theory_exam_finished', 'sim_exam_finished'] as const;

/** Max events per `POST /api/track` batch. */
export const TRACK_BATCH_MAX = 20;

/** Max events one `anonId` may send in any 60 s window; above it the API answers 429. */
export const TRACK_EVENTS_PER_MINUTE = 60;

export const UsageEventKindSchema = z.enum(USAGE_EVENT_KINDS);
export type UsageEventKind = z.infer<typeof UsageEventKindSchema>;

/** Random device id: UUID-shaped hex (any version), normalised to lower case. */
export const AnonIdSchema = z
  .string()
  .toLowerCase()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'anonId must be a UUID');
export type AnonId = z.infer<typeof AnonIdSchema>;

/**
 * License class code (`B`, `C1`, `D` ...). A code shape, not an enum: classes come from the content
 * registry (`defineLicenseClass`), and a new class must not make analytics reject events.
 */
export const UsageLicenseClassSchema = z.string().regex(/^[A-Z][A-Z0-9]{0,3}$/, 'Invalid class');

const isFinished = (kind: UsageEventKind) =>
  (USAGE_FINISHED_KINDS as readonly UsageEventKind[]).includes(kind);

export const UsageEventSchema = z
  .strictObject({
    kind: UsageEventKindSchema,
    licenseClass: UsageLicenseClassSchema.optional(),
    /** Exam result; only on `*_finished` kinds. */
    passed: z.boolean().optional(),
  })
  .refine((e) => e.passed === undefined || isFinished(e.kind), {
    message: 'passed is only allowed on *_finished events',
    path: ['passed'],
  });
export type UsageEvent = z.infer<typeof UsageEventSchema>;

export const TrackRequestSchema = z.strictObject({
  anonId: AnonIdSchema,
  events: z.array(UsageEventSchema).min(1).max(TRACK_BATCH_MAX),
});
export type TrackRequest = z.infer<typeof TrackRequestSchema>;

/** 202 body: events stored (0 when analytics is unavailable; the client never retries on that). */
export const TrackResponseSchema = z.strictObject({ accepted: z.number().int().nonnegative() });
export type TrackResponse = z.infer<typeof TrackResponseSchema>;
